import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MousePointerClick, CircleDollarSign, ShoppingBasket, Gem, Star, Facebook, Instagram, Gift, Heart, Sparkles, Check } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const VIP_ICONS: Record<string, any> = { sign_up: MousePointerClick, earn: CircleDollarSign, redeem: ShoppingBasket, gem: Gem, star: Star, gift: Gift, heart: Heart, sparkle: Sparkles };
export const vipId = (b: any) => (b.nav_label || b.heading || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const cash = (n: number) => `$${Number(n || 0).toFixed(2)}`;
const short = (n: number) => `$${Number(n || 0) % 1 ? Number(n).toFixed(2) : Number(n)}`;
const pill = "inline-flex items-center justify-center min-w-[150px] rounded-full border border-neutral-900 px-8 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-neutral-900 hover:text-white transition-colors";

export function useVip() {
  const { user } = useAuth();
  const [prog, setProg] = useState<any>(null);
  const [me, setMe] = useState<any>(null);
  const load = () => (user
    ? api.get("/rewards/me").then((r) => { setMe(r.data); setProg(r.data.program); })
    : api.get("/rewards/program").then((r) => { setMe(null); setProg(r.data); })).catch(() => {});
  useEffect(() => {
    if (user === null) return;
    load();
    const on = () => load();
    window.addEventListener("vip-refresh", on);
    return () => window.removeEventListener("vip-refresh", on);
  }, [user?.id, user === false]);
  return { user, prog, me, reload: () => window.dispatchEvent(new Event("vip-refresh")) };
}

export function VipHero({ b }: any) {
  const { user, me } = useVip();
  return (
    <section className="grid md:grid-cols-2 bg-[#EFEEEC] overflow-hidden" data-testid="vip-hero">
      <div className="order-2 md:order-1 flex flex-col items-center justify-center text-center px-6 py-14 md:py-20">
        <h1 className="font-display uppercase tracking-[0.08em] text-4xl sm:text-5xl rise" data-testid="vip-hero-title">{b.heading}</h1>
        {b.body && <p className="mt-6 max-w-md text-[15px] leading-relaxed rise rise-2">{b.body}</p>}
        {user ? (
          <div className="mt-9 rise rise-3" data-testid="vip-hero-member">
            <p className="eyebrow text-neutral-500">Your Somnio VIP balance</p>
            <p className="font-display text-4xl mt-2" data-testid="vip-hero-balance">{money(me?.available || 0)}</p>
            {me?.pending > 0 && <p className="text-xs text-neutral-500 mt-1">+ {money(me.pending)} pending</p>}
            <div className="mt-7 flex flex-wrap justify-center gap-5">
              <Link to="/account?tab=vip" className={pill} data-testid="vip-view-rewards-button">My Rewards</Link>
              <Link to="/collections/all" className={pill} data-testid="vip-shop-button">Shop Now</Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 flex flex-wrap justify-center gap-6 rise rise-3">
            <Link to={`/register?next=${encodeURIComponent("/account?tab=vip")}`} className={pill} data-testid="vip-join-button">{b.join_label || "Join Now"}</Link>
            <Link to="/login?next=/pages/rewards" className={pill} data-testid="vip-login-button">{b.login_label || "Login"}</Link>
          </div>
        )}
      </div>
      <div className="order-1 md:order-2 h-[42vh] md:h-auto md:min-h-[520px]">{b.image && <img src={b.image} alt="" className="w-full h-full object-cover object-center" />}</div>
    </section>
  );
}

export function VipNav({ all }: any) {
  const items = (all || []).filter((x: any) => ["vip_steps", "vip_earn", "vip_faq"].includes(x.type) && (x.heading || x.nav_label));
  const [active, setActive] = useState(items[0] ? vipId(items[0]) : "");
  useEffect(() => {
    const obs = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && setActive(e.target.id)), { rootMargin: "-40% 0px -55% 0px" });
    items.forEach((x: any) => { const el = document.getElementById(vipId(x)); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [all]);
  if (!items.length) return null;
  return (
    <nav className="sticky top-16 md:top-[124px] z-30 bg-[var(--c-bg)] border-b border-neutral-200" data-testid="vip-nav">
      <div className="flex justify-center gap-7 md:gap-10 overflow-x-auto px-5">
        {items.map((x: any) => { const id = vipId(x); return (
          <button key={id} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })} data-testid={`vip-nav-${id}`}
            className={`py-4 text-[11px] md:text-xs tracking-[0.14em] uppercase whitespace-nowrap border-b-2 -mb-px transition-colors ${active === id ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>{x.nav_label || x.heading}</button>
        ); })}
      </div>
    </nav>
  );
}

export function VipSteps({ b }: any) {
  return (
    <section id={vipId(b)} className="scroll-mt-48 max-w-[1100px] mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="vip-steps">
      <div className="text-center max-w-xl mx-auto">
        <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl">{b.heading}</h2>
        {b.body && <div className="mt-6 text-sm md:text-[15px] leading-relaxed"><Markdown text={b.body} /></div>}
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {(b.items || []).map((s: any, i: number) => { const Icon = VIP_ICONS[s.icon] || Sparkles; return (
          <div key={i} className="border border-neutral-800 px-8 py-14 text-center min-h-[300px] md:min-h-[340px] flex flex-col items-center transition-transform duration-500 hover:-translate-y-1" data-testid={`vip-step-${i + 1}`}>
            <Icon size={58} strokeWidth={0.9} />
            <p className="mt-10 text-sm tracking-[0.12em] uppercase">{s.title}</p>
            <p className="mt-5 text-[13px] leading-relaxed text-neutral-700">{s.body}</p>
          </div>
        ); })}
      </div>
    </section>
  );
}

function EarnTile({ t, onClick }: any) {
  const inner = (
    <>
      <span className="h-24 w-24 rounded-full bg-white flex items-center justify-center transition-transform duration-500 group-hover:scale-105"><t.Icon size={40} strokeWidth={1} /></span>
      <p className="mt-6 text-sm tracking-[0.12em] uppercase">{t.title}</p>
      <div className="mt-5 text-[13px] leading-snug space-y-0.5">{t.lines.map((l: string) => <p key={l}>{l}</p>)}</div>
      {t.claimed ? <p className="mt-auto pt-5 text-[10px] uppercase tracking-widest text-emerald-700 flex items-center gap-1" data-testid={`vip-earn-${t.k}-claimed`}><Check size={12} /> Earned</p>
        : <p className="mt-auto pt-5 text-[10px] uppercase tracking-widest underline underline-offset-4 text-neutral-600 group-hover:text-neutral-900">{t.cta}</p>}
    </>
  );
  const cls = "group bg-[var(--c-muted)] px-6 pt-5 pb-7 text-center flex flex-col items-center min-h-[250px] transition-transform duration-500 hover:-translate-y-1";
  if (t.to) return <Link to={t.to} className={cls} data-testid={`vip-earn-${t.k}`}>{inner}</Link>;
  return <button type="button" onClick={onClick} disabled={t.claimed || !onClick} className={`${cls} disabled:hover:translate-y-0 disabled:cursor-default`} data-testid={`vip-earn-${t.k}`}>{inner}</button>;
}

export function VipEarn({ b }: any) {
  const { user, prog, me, reload } = useVip();
  const nav = useNavigate();
  const follow = async (k: string) => {
    if (!user) { toast("Create your free account or sign in to earn this reward"); nav("/login?next=/pages/rewards"); return; }
    window.open(prog[`${k}_url`], "_blank", "noopener");
    try { const r = await api.post(`/rewards/social/${k}`); if (!r.data.already) toast.success(`${money(r.data.amount)} added to your Somnio VIP balance`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tiles = prog ? [
    { k: "purchase", Icon: Gem, title: "Purchase", lines: [`${cash(prog.earn_per)} for every ${short(prog.spend_per)} spent`], to: "/collections/all", cta: "Shop now", on: prog.earn_per > 0 },
    { k: "review", Icon: Star, title: "Leave a Review", lines: [cash(prog.review), `${cash(prog.review_photo)} with photo`, `${cash(prog.review_video)} with video`], cta: "On any piece you own", on: prog.review > 0 || prog.review_photo > 0 },
    { k: "facebook", Icon: Facebook, title: "Follow on Facebook", lines: [cash(prog.facebook)], cta: "Follow & earn", on: prog.facebook > 0, claimed: me?.claimed?.facebook },
    { k: "instagram", Icon: Instagram, title: "Follow on Instagram", lines: [cash(prog.instagram)], cta: "Follow & earn", on: prog.instagram > 0, claimed: me?.claimed?.instagram },
  ].filter((t) => t.on) : [];
  return (
    <section id={vipId(b)} className="scroll-mt-48 max-w-[1100px] mx-auto px-5 md:px-8 py-16 md:py-20" data-testid="vip-earn">
      <div className="text-center max-w-xl mx-auto">
        <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl">{b.heading}</h2>
        {b.body && <p className="mt-6 text-sm md:text-[15px] leading-relaxed">{b.body}</p>}
      </div>
      <div className={`mt-14 grid grid-cols-2 gap-4 md:gap-6 ${tiles.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
        {tiles.map((t) => <EarnTile key={t.k} t={t} onClick={t.k === "facebook" || t.k === "instagram" ? () => follow(t.k) : undefined} />)}
      </div>
    </section>
  );
}

export function VipFaq({ b }: any) {
  return (
    <section id={vipId(b)} className="scroll-mt-48 bg-[#F4F4F3] mt-12" data-testid="vip-faq">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-20">
        <h2 className="font-display uppercase tracking-[0.06em] text-3xl md:text-4xl">{b.heading}</h2>
        <Accordion type="single" collapsible className="mt-10 grid md:grid-cols-2 gap-x-6 gap-y-5 items-start">
          {(b.items || []).map((f: any, i: number) => (
            <AccordionItem key={i} value={`q${i}`} className="border border-neutral-900 rounded-[10px] bg-white px-5" data-testid={`vip-faq-${i}`}>
              <AccordionTrigger className="text-left text-sm font-normal py-3.5 hover:no-underline" data-testid={`vip-faq-q-${i}`}>{f.question}</AccordionTrigger>
              <AccordionContent className="text-[13px] leading-relaxed text-neutral-700">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
